// Global Instructions Rule Applied!

import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import RichTextEditor from '../../RichTextEditor.jsx'
import { FieldValue, textFieldValue } from '../types.ts'

export interface RichTextInputProps {
  value: FieldValue
  onChange: (value: FieldValue) => void
  onLiveChange?: (value: string) => void
  placeholder?: string
  editorConfig?: any
  size?: 'small' | 'middle' | 'large'
  onKeyDown?: (e: React.KeyboardEvent) => void
}

/** How many of the editor's own reports to remember for echo detection. */
const EMITTED_HISTORY = 100

export const RichTextInput = forwardRef<any, RichTextInputProps>(
  ({ value, onChange, onLiveChange, placeholder, editorConfig, size, onKeyDown }, ref) => {
    const incoming = String(textFieldValue(value))

    // Every keystroke feeds the field's working value (onChange), so a confirm
    // or a save clicked straight after typing commits what is on screen. That
    // value round-trips through InputManager and comes back here as `value`
    // a render later — by which time the editor may have moved on, and
    // re-applying the echo would revert keystrokes and jump the caret. So the
    // editor's controlled value only follows `value` when it is genuinely
    // external (record loaded, form reset), i.e. not something the editor
    // itself reported since the last external value.
    const emittedRef = useRef<string[]>([])
    const [editorValue, setEditorValue] = useState(incoming)

    useEffect(() => {
      if (emittedRef.current.includes(incoming)) return
      emittedRef.current = []
      setEditorValue(incoming)
    }, [incoming])

    const remember = (html: string): void => {
      const emitted = emittedRef.current
      emitted.push(html)
      if (emitted.length > EMITTED_HISTORY) emitted.splice(0, emitted.length - EMITTED_HISTORY)
    }

    // Blur commit
    const handleChange = useCallback(
      (newContent: string) => {
        remember(newContent)
        onChange(newContent)
      },
      [onChange]
    )

    // Live typing
    const handleLiveChange = useCallback(
      (newContent: string) => {
        remember(newContent)
        onLiveChange?.(newContent)
        onChange(newContent)
      },
      [onLiveChange, onChange]
    )

    return (
      <div
        className='w-full h-full flex flex-col min-h-0'
        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        tabIndex={-1}
      >
        <RichTextEditor
          // ref={editorRef}
          value={editorValue}
          onChange={handleChange}
          onLiveChange={handleLiveChange}
          placeholder={placeholder || ''}
          config={editorConfig}
          className='h-full'
          style={{ height: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}
          onKeyDown={onKeyDown}
        />
      </div>
    )
  }
)

RichTextInput.displayName = 'RichTextInput'
